import { Briefcase, Building2, UserCircle, Wallet } from "lucide-react"
import { useTranslation } from "react-i18next"

import { StatCard } from "@/components/admin/stat-card"
import type { AdminAnalyticsKpis } from "@/types/admin-analytics"

/**
 * OKS-66 — 4-tile KPI strip for the Platform Analytics page.
 *
 * Order: Brands · Agencies · Creators · Active brands (UX Spec §2 lock).
 * The first three tiles are snapshot (window-invariant); the fourth
 * (`active_brands`) carries the "in last {window}" caption derived
 * from `kpis.active_brands_window` (server-truth — may differ from
 * URL state if off-whitelist was coerced).
 *
 * Responsive grid: `sm:grid-cols-2 lg:grid-cols-4` — mobile 2×2,
 * desktop 1×4. Matches OKS-15 Overview StatCard grid.
 */
export interface AnalyticsKpiStripProps {
  kpis: AdminAnalyticsKpis
}

export function AnalyticsKpiStrip({ kpis }: AnalyticsKpiStripProps) {
  const { t } = useTranslation()

  // Explicit per-value caption resolution — i18next-health-checker traces
  // literal `t("...")` calls; a dict lookup wouldn't be visible to it.
  const activeBrandsCaption =
    kpis.active_brands_window === "30d"
      ? t("admin.analytics.kpis.active_brands.caption_30d")
      : kpis.active_brands_window === "90d"
        ? t("admin.analytics.kpis.active_brands.caption_90d")
        : t("admin.analytics.kpis.active_brands.caption_all")

  return (
    <section
      aria-label={t("admin.analytics.kpis.section_aria")}
      data-testid="admin-analytics-kpi-strip"
      className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      <StatCard
        testId="admin-analytics-kpi-brands"
        icon={Building2}
        label={t("admin.analytics.kpis.brands.label")}
        value={kpis.brands}
      />
      <StatCard
        testId="admin-analytics-kpi-agencies"
        icon={Briefcase}
        label={t("admin.analytics.kpis.agencies.label")}
        value={kpis.agencies}
      />
      <StatCard
        testId="admin-analytics-kpi-creators"
        icon={UserCircle}
        label={t("admin.analytics.kpis.creators.label")}
        value={kpis.creators}
      />
      <StatCard
        testId="admin-analytics-kpi-active-brands"
        icon={Wallet}
        label={t("admin.analytics.kpis.active_brands.label")}
        value={kpis.active_brands}
        caption={activeBrandsCaption}
      />
    </section>
  )
}
